import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  Building2,
  Check,
  ChevronDown,
  SearchX,
  ShieldCheck,
  User,
  UserCog,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TenantRole } from './roles';

/**
 * The role is derived from the generated contract (`AccountTenantDto.role`, in
 * `src/services/generated/api.ts`, generated from `openapi.json`) rather than
 * redeclared by hand: a change to the backend's role vocabulary breaks the
 * typecheck here instead of silently rendering an empty role.
 *
 * This is the role AT THE ORGANISATION level: `'owner' | 'admin' | 'member'`
 * (the wire values are in English; they are labelled in Spanish here). What a
 * person can do with a COMPANY is another vocabulary and lives in `CompanyRole`
 * — the SII authorises per company. See `docs/USUARIOS_Y_ROLES.md`.
 */
export type { TenantRole } from './roles';

export interface TenantOption {
  id: string;
  slug: string;
  name: string;
  role: TenantRole;
}

export interface TenantPickerProps {
  tenants: readonly TenantOption[];
  value: string | null;
  onChange: (tenantId: string) => void;
  /** `list` for screen 3; `menu` for the shell's switcher. */
  layout?: 'list' | 'menu';
  className?: string;
}

/**
 * The `role` gates actions further on (only an `owner` mints API keys): hiding
 * it here makes the later 403 look like a product bug rather than a known rule.
 * It goes with an icon as well as text — never a tint alone — because WCAG
 * 1.4.1 asks that no state be distinguished by colour alone, and here there is
 * not even colour: the icon's shape already tells them apart.
 */
const ROLE_PRESENTATION: Record<
  TenantRole,
  { label: string; icon: typeof ShieldCheck }
> = {
  owner: { label: 'Propietario', icon: ShieldCheck },
  admin: { label: 'Administrador', icon: UserCog },
  member: { label: 'Miembro', icon: User },
};

function RoleTag({
  role,
  className,
}: {
  role: TenantRole;
  className?: string;
}): React.JSX.Element {
  const { label, icon: Icon } = ROLE_PRESENTATION[role];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-caption text-fg-muted',
        className,
      )}
    >
      <Icon aria-hidden="true" className="size-icon-sm" />
      {label}
    </span>
  );
}

function TenantSummary({
  tenant,
}: {
  tenant: TenantOption;
}): React.JSX.Element {
  return (
    <span className="flex min-w-0 flex-col items-start">
      <span className="truncate text-body-s font-medium text-fg">
        {tenant.name}
      </span>
      <span className="truncate text-caption text-fg-muted">{tenant.slug}</span>
    </span>
  );
}

/** The switcher offers a search field only when there are MORE than this many
 *  organisations: below that, scanning the list is faster than typing. */
const SEARCH_THRESHOLD = 7;

/** Lower-case and accent-free, so «pacifico» finds «Pacífico». */
function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

interface LayoutProps {
  tenants: readonly TenantOption[];
  value: string | null;
  onChange: (tenantId: string) => void;
  className?: string;
}

/**
 * Screen 3: an explicit decision between organisations, not a transient menu
 * that opens and closes. That is why it is announced as a `radiogroup` — each
 * organisation is a mutually exclusive option, always visible — with roving
 * focus (`roving tabindex`): only one option is tabbable at a time and the
 * arrows move the focus AND select at once, which is the standard WAI-ARIA
 * pattern for a radio group.
 */
function TenantPickerList({
  tenants,
  value,
  onChange,
  className,
}: LayoutProps): React.JSX.Element {
  const optionRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const selectedIndex = tenants.findIndex((tenant) => tenant.id === value);

  function moveFocusTo(nextIndex: number): void {
    const next = tenants[nextIndex];
    if (!next) return;
    onChange(next.id);
    optionRefs.current.get(next.id)?.focus();
  }

  function handleKeyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        moveFocusTo((index + 1) % tenants.length);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        moveFocusTo((index - 1 + tenants.length) % tenants.length);
        break;
      case 'Home':
        event.preventDefault();
        moveFocusTo(0);
        break;
      case 'End':
        event.preventDefault();
        moveFocusTo(tenants.length - 1);
        break;
      default:
        break;
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label="Organización"
      className={cn('flex flex-col gap-stack-sm', className)}
    >
      {tenants.map((tenant, index) => {
        const selected = tenant.id === value;
        // When nothing has been chosen yet (`value=null`), the first option stays
        // tabbable so Tab does not skip the whole control.
        const isTabbable = selected || (selectedIndex === -1 && index === 0);

        return (
          <button
            key={tenant.id}
            ref={(el) => {
              if (el) optionRefs.current.set(tenant.id, el);
              else optionRefs.current.delete(tenant.id);
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={isTabbable ? 0 : -1}
            onClick={() => onChange(tenant.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'flex min-h-touch items-center justify-between gap-inset rounded-md border p-inset text-start focus-ring',
              selected
                ? 'border-action bg-action-subtle'
                : 'border-border hover:bg-subtle',
            )}
          >
            <TenantSummary tenant={tenant} />
            <span className="flex shrink-0 items-center gap-inset-sm">
              <RoleTag role={tenant.role} />
              {selected ? (
                <Check
                  aria-hidden="true"
                  className="size-icon-sm text-action"
                />
              ) : (
                <span aria-hidden="true" className="size-icon-sm" />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// `z-50`: the popover layer of the shell's z-scale (docs/FRONTEND_COMPONENT_INVENTORY.md
// §5.5). The popup is PORTALLED to `document.body`, so it is no longer clipped by
// the header or hidden under the drawer (spec R3(c)).
//
// The width cap is the smaller of `popup-md` and the room Radix measured beside
// the trigger (`--radix-popover-content-available-width`, which already takes
// `collisionPadding` off): below `sm` the trigger sits in the 256 px drawer, and
// with long organisation names `popup-md` alone let the menu run ~240 px past
// the 393 px viewport, its options cut off at the screen edge. `select.tsx`
// caps its popup the same way.
const POPUP_CLASS =
  'z-50 flex min-w-[var(--radix-popover-trigger-width)] max-w-[min(var(--spacing-popup-md),var(--radix-popover-content-available-width))] flex-col gap-stack-sm rounded-md border border-border-strong bg-surface p-inset-sm shadow-overlay';

/**
 * The shell strip's switcher: it opens and closes over the rest of the UI, so it
 * is a pop-up `listbox`, rendered by a Radix `Popover` into a portal (z-50) so the
 * header and the drawer can neither clip nor cover it, and closed whenever the
 * shell's drawer opens or shuts (`appshell:drawer-open` / `-close`). The trigger
 * reads «Organización: <name>» to assistive technology. It is a pop-up `listbox` (the "read-only combobox" pattern), not a permanent
 * radiogroup. The DOM focus stays on the listbox itself while it is open — it
 * does not jump from option to option — and `aria-activedescendant` announces
 * which one is highlighted; it is the pattern that avoids moving the real focus
 * outside the container every time the user presses an arrow.
 *
 * With MORE than `SEARCH_THRESHOLD` (7) organisations the popup gains a search
 * field («Buscar organización») above the list, matching name and slug without
 * regard to case or accents. Focus then stays on the field (a combobox whose
 * `aria-activedescendant` names the highlighted option) and the arrows, Enter,
 * Escape and Tab behave as in the plain listbox; Home/End and Space belong to
 * the text field. With no match the list is replaced by a text message.
 */
function TenantPickerMenu({
  tenants,
  value,
  onChange,
  className,
}: LayoutProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const listboxId = React.useId();

  const selectedTenant = tenants.find((tenant) => tenant.id === value) ?? null;
  const searchable = tenants.length > SEARCH_THRESHOLD;
  const needle = normalizeSearch(query.trim());
  const visible =
    searchable && needle !== ''
      ? tenants.filter((tenant) =>
          normalizeSearch(`${tenant.name} ${tenant.slug}`).includes(needle),
        )
      : tenants;

  // The shell's drawer and this popup are both floating layers: opening the
  // drawer closes the popup (spec R3(c)), and so does shutting it, because the
  // account slot — and with it this picker — is unmounted or made inert with it.
  // The event names are the ones `AppShell` dispatches on `document`.
  React.useEffect(() => {
    function close(): void {
      setOpen(false);
    }
    document.addEventListener('appshell:drawer-open', close);
    document.addEventListener('appshell:drawer-close', close);
    return () => {
      document.removeEventListener('appshell:drawer-open', close);
      document.removeEventListener('appshell:drawer-close', close);
    };
  }, []);

  // The popup mounts inside a portal, so its refs only exist once Radix says it
  // has opened: focus is placed from there and not from an effect on `open`.
  function focusPopup(): void {
    if (searchable) searchRef.current?.focus();
    else listRef.current?.focus();
  }

  function openMenu(): void {
    const currentIndex = tenants.findIndex((tenant) => tenant.id === value);
    setActiveIndex(currentIndex === -1 ? 0 : currentIndex);
    setQuery('');
    setOpen(true);
  }

  function closeMenu(returnFocus: boolean): void {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function commitActive(): void {
    const tenant = visible[activeIndex];
    if (tenant) onChange(tenant.id);
    closeMenu(true);
  }

  function handleTriggerKeyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void {
    // Enter/Space already open through the button's native `onClick`; only the
    // arrows are covered here, which a native button does not translate to a click.
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openMenu();
    }
  }

  function handleListKeyDown(
    event: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>,
  ): void {
    const inSearch = event.target instanceof HTMLInputElement;
    const count = visible.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count > 0) setActiveIndex((index) => (index + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count > 0) setActiveIndex((index) => (index - 1 + count) % count);
        break;
      case 'Home':
        // In the search field Home/End move the caret, as a text field should.
        if (inSearch) break;
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        if (inSearch) break;
        event.preventDefault();
        setActiveIndex(count - 1);
        break;
      case ' ':
        // A space is part of the query while typing in the search field.
        if (inSearch) break;
        event.preventDefault();
        commitActive();
        break;
      case 'Enter':
        event.preventDefault();
        commitActive();
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu(true);
        break;
      case 'Tab':
        // It does not trap the focus: it lets it take its natural course, it only closes.
        closeMenu(false);
        break;
      default:
        break;
    }
  }

  const activeTenant = visible[activeIndex];
  const activeOptionId = activeTenant
    ? `${listboxId}-option-${activeTenant.id}`
    : undefined;

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => (next ? openMenu() : closeMenu(false))}
    >
      <div className={cn('inline-block', className)}>
        <PopoverPrimitive.Trigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-label={
              selectedTenant
                ? `Organización: ${selectedTenant.name}`
                : undefined
            }
            onKeyDown={handleTriggerKeyDown}
            /* ONE LINE, and borderless. The strip is `h-row` — 48px, fixed — and
             * this trigger used to stack the name over the slug inside a bordered
             * box with `py-inset-sm`: 8 + 21 + 17 + 8 plus the border is ~56px
             * inside 48, so the box bled above and below the header and its own
             * border was clipped by both edges. It is the same defect the review
             * logged against the COMPANY selector (item 6), fixed there by the
             * `header` variant of `Select`; the switcher is a different component
             * and never got it.
             *
             * So: no border, a hover tint instead, and the slug drops to the menu —
             * which is where it earns its place, telling two similarly-named
             * organisations apart while you choose between them. `min-h-touch`
             * stays: 48px is the target (D14), and with no border there is nothing
             * left to touch the strip's edges.
             *
             * `min-w-0 max-w-64` with `truncate` on the name is the other half. The
             * account slot contributes its content's width to the header's minimum,
             * so «Comercializadora e Inversiones del Pacífico Sur SpA» pushed
             * `document.scrollWidth` to 591px at a 390px viewport — the whole page
             * scrolling sideways, exactly what `AppShell` documents for the left
             * cluster and had never applied to this one. */
            className="flex min-h-touch min-w-0 max-w-64 items-center gap-inset-sm rounded-md px-inset-sm text-start hover:bg-action-subtle focus-ring"
          >
            <Building2
              aria-hidden="true"
              className="size-icon-sm shrink-0 text-fg-muted"
            />
            {selectedTenant ? (
              <span className="truncate text-body-s font-medium text-fg">
                {selectedTenant.name}
              </span>
            ) : (
              <span className="truncate text-body-s text-fg-muted">
                Selecciona una organización
              </span>
            )}
            {/* The role gates actions further on, so it is worth the width where
             * there is width — but not on a phone, where «Administrador» costs more
             * than the organisation's own name. Below `sm` it drops, the same call
             * `AccountMenu` already makes for the email and the logout label, and
             * it is still on every option one tap away. */}
            {selectedTenant ? (
              <RoleTag
                role={selectedTenant.role}
                className="hidden sm:inline-flex"
              />
            ) : null}
            <ChevronDown
              aria-hidden="true"
              className="size-icon-sm shrink-0 text-fg-muted"
            />
          </button>
        </PopoverPrimitive.Trigger>
      </div>

      <PopoverPrimitive.Portal>
        {/* `align="end"`, not the default left edge: this menu lives at the
         * right end of the strip and is wider than its trigger — anchored left
         * it grows into the viewport's edge and the options are cut off. Aligned
         * to its end it grows the only way there is room; `collisionPadding`
         * keeps it off the edge on a phone. */}
        <PopoverPrimitive.Content
          align="end"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            focusPopup();
          }}
          // Focus is returned by hand (`closeMenu(true)`): Tab must NOT pull it
          // back to the trigger, and an outside click goes where it was aimed.
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            closeMenu(true);
          }}
          className={cn(POPUP_CLASS, searchable && 'gap-inset-sm')}
          onKeyDown={searchable ? handleListKeyDown : undefined}
        >
          {searchable ? (
            <input
              ref={searchRef}
              type="search"
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-activedescendant={activeOptionId}
              aria-label="Buscar organización"
              placeholder="Buscar organización"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              className="min-h-touch w-full rounded-md border border-border bg-surface px-inset-sm text-body-s text-fg placeholder:text-fg-muted focus-ring"
            />
          ) : null}
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label="Organización"
            aria-activedescendant={searchable ? undefined : activeOptionId}
            tabIndex={searchable ? undefined : -1}
            onKeyDown={searchable ? undefined : handleListKeyDown}
            className={searchable ? 'flex flex-col gap-stack-sm' : undefined}
          >
            {visible.map((tenant, index) => {
              const selected = tenant.id === value;
              const active = index === activeIndex;

              return (
                <div
                  key={tenant.id}
                  id={`${listboxId}-option-${tenant.id}`}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    onChange(tenant.id);
                    closeMenu(true);
                  }}
                  className={cn(
                    'flex min-h-touch cursor-pointer items-center justify-between gap-inset rounded-md p-inset-sm',
                    active ? 'bg-action-subtle' : 'hover:bg-subtle',
                  )}
                >
                  <TenantSummary tenant={tenant} />
                  <span className="flex shrink-0 items-center gap-inset-sm">
                    <RoleTag role={tenant.role} />
                    {selected ? (
                      <Check
                        aria-hidden="true"
                        className="size-icon-sm text-action"
                      />
                    ) : (
                      <span aria-hidden="true" className="size-icon-sm" />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {/* Mounted for as long as the search field is, and only its text
              changes: a live region that appears in the same render as its
              content is exactly the one many screen readers do not announce. */}
          {searchable ? (
            <p
              role="status"
              className={
                visible.length === 0
                  ? 'flex items-center gap-inset-sm p-inset-sm text-body-s text-fg-muted'
                  : 'sr-only'
              }
            >
              {visible.length === 0 ? (
                <>
                  <SearchX
                    aria-hidden="true"
                    className="size-icon-sm shrink-0"
                  />
                  Sin organizaciones que coincidan con la búsqueda.
                </>
              ) : null}
            </p>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/**
 * Organisation (tenant) picker, in two genuinely different forms — not the same
 * list with a different `className`: `list` is an explicit, permanent decision
 * (screen 3); `menu` is a switcher that opens and closes (the shell's strip). See
 * the full contract in `docs/FRONTEND_COMPONENT_INVENTORY.md` §5.4.
 * `docs/FRONTEND_COMPONENT_INVENTORY.md` §5.4.
 *
 * It fetches nothing and dispatches no session action: it only reports through
 * `onChange`. Whoever uses it decides what to do with the chosen id (§3,
 * convention 4).
 */
export function TenantPicker({
  tenants,
  value,
  onChange,
  layout = 'list',
  className,
}: TenantPickerProps): React.JSX.Element {
  if (layout === 'menu') {
    return (
      <TenantPickerMenu
        tenants={tenants}
        value={value}
        onChange={onChange}
        className={className}
      />
    );
  }

  return (
    <TenantPickerList
      tenants={tenants}
      value={value}
      onChange={onChange}
      className={className}
    />
  );
}
